import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  WorkflowStorageOwnerService,
  WorkflowStorageRepository,
  workflowInstanceKeyToString,
  type WorkflowStorageOwner,
} from './storage';
import type {
  NewQueuedWorkflowCompletion,
  NewQueuedWorkflowSubmission,
  QueuedWorkflowCompletion,
  QueuedWorkflowSubmission,
} from './workflow-offline.types';

export type {
  NewQueuedWorkflowCompletion,
  NewQueuedWorkflowSubmission,
  QueuedWorkflowCompletion,
  QueuedWorkflowSubmission,
} from './workflow-offline.types';

/**
 * Офлайн-очереди регламентов (сабмиты форм и завершение).
 *
 * Каждая задача — отдельная запись с владельцем (окружение + пользователь):
 * задачи одного пользователя не видны и не исполняются под другим.
 * Ошибки записи пробрасываются: вызывающий код не должен сообщать
 * «будет отправлено», если задача не сохранилась.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowOfflineService {
  private readonly storage = inject(WorkflowStorageRepository);
  private readonly ownerService = inject(WorkflowStorageOwnerService);
  private readonly queueChangedSubject = new Subject<void>();
  private readonly queueUpdatedSubject = new Subject<void>();

  /** Новая задача добавлена — процессору стоит запустить проход */
  readonly queueChanged$ = this.queueChangedSubject.asObservable();

  /** Любое изменение очереди (добавление, смена статуса, удаление) — для UI */
  readonly queueUpdated$ = this.queueUpdatedSubject.asObservable();

  async addSubmissionToQueue(
    submission: NewQueuedWorkflowSubmission,
  ): Promise<QueuedWorkflowSubmission> {
    const owner = this.ownerService.requireOwner();
    const id = crypto.randomUUID();
    const queued: QueuedWorkflowSubmission = {
      id,
      operationId: submission.operationId ?? id,
      instanceKey: workflowInstanceKeyToString(submission.requestNumber, {
        requestWorkflowId: submission.workflowId,
        nodeId: submission.nodeId,
        formId: submission.formId,
      }),
      requestNumber: submission.requestNumber,
      workflowId: submission.workflowId,
      nodeId: submission.nodeId,
      formId: submission.formId,
      body: submission.body,
      timestamp: submission.timestamp,
      attempts: 0,
      nextAttemptAt: 0,
      status: 'pending',
    };

    await this.saveSubmission(queued, owner);
    this.queueChangedSubject.next();

    return queued;
  }

  /**
   * Сохранить изменённое состояние задачи (попытки, статус, ошибка)
   */
  async saveSubmission(
    submission: QueuedWorkflowSubmission,
    owner: WorkflowStorageOwner,
  ): Promise<void> {
    await this.storage.put(
      owner,
      'wf-queue-submission',
      submission.id,
      submission,
      {
        requestNumber: submission.requestNumber,
      },
    );
    this.queueUpdatedSubject.next();
  }

  /**
   * Задачи владельца (по умолчанию — текущего) в порядке создания.
   * Процессор передаёт владельца явно, зафиксированного на всё время обработки.
   */
  async getSubmissionQueue(
    owner: WorkflowStorageOwner | null = this.ownerService.getOwner(),
  ): Promise<QueuedWorkflowSubmission[]> {
    if (!owner) {
      return [];
    }

    const records = await this.storage.list<QueuedWorkflowSubmission>(
      owner,
      'wf-queue-submission',
    );

    return records.map(record => normalizeSubmission(record.value));
  }

  async removeSubmissionFromQueue(
    id: QueuedWorkflowSubmission['id'],
    owner: WorkflowStorageOwner | null = this.ownerService.getOwner(),
  ): Promise<void> {
    if (!owner) {
      return;
    }

    await this.storage.delete(owner, 'wf-queue-submission', id);
    this.queueUpdatedSubject.next();
  }

  async clearSubmissionQueue(): Promise<void> {
    const queue = await this.getSubmissionQueue();

    await Promise.all(
      queue.map(item => this.removeSubmissionFromQueue(item.id)),
    );
  }

  /**
   * Есть ли неотправленные задачи для экземпляра формы (онлайн-сохранение должно встать за ними)
   */
  async hasQueuedSubmissionsForInstance(instanceKey: string): Promise<boolean> {
    const queue = await this.getSubmissionQueue();

    return queue.some(item => item.instanceKey === instanceKey);
  }

  /**
   * Добавить завершение регламентов заявки (повторное добавление для той же заявки перезаписывает задачу).
   */
  async addCompletionToQueue(
    completion: NewQueuedWorkflowCompletion,
  ): Promise<void> {
    const owner = this.ownerService.requireOwner();

    await this.saveCompletion(
      {
        requestNumber: completion.requestNumber,
        timestamp: completion.timestamp,
        operationId: completion.operationId ?? crypto.randomUUID(),
        attempts: 0,
        nextAttemptAt: 0,
        status: 'pending',
      },
      owner,
    );
    this.queueChangedSubject.next();
  }

  async saveCompletion(
    completion: QueuedWorkflowCompletion,
    owner: WorkflowStorageOwner,
  ): Promise<void> {
    await this.storage.put(
      owner,
      'wf-queue-completion',
      completion.requestNumber,
      completion,
      { requestNumber: completion.requestNumber },
    );
    this.queueUpdatedSubject.next();
  }

  async getCompletionQueue(
    owner: WorkflowStorageOwner | null = this.ownerService.getOwner(),
  ): Promise<QueuedWorkflowCompletion[]> {
    if (!owner) {
      return [];
    }

    const records = await this.storage.list<StoredCompletion>(
      owner,
      'wf-queue-completion',
    );
    const completions: QueuedWorkflowCompletion[] = [];

    for (const record of records) {
      const completion = normalizeCompletion(record.value);

      // Запись предыдущей версии без operationId: UUID генерируется один раз и сохраняется,
      // чтобы все повторы шли с одним идентификатором (сервер принимает только UUID)
      if (!record.value.operationId) {
        await this.saveCompletion(completion, owner);
      }

      completions.push(completion);
    }

    return completions;
  }

  async removeCompletionFromQueue(
    requestNumber: string,
    owner: WorkflowStorageOwner | null = this.ownerService.getOwner(),
  ): Promise<void> {
    if (!owner) {
      return;
    }

    await this.storage.delete(owner, 'wf-queue-completion', requestNumber);
    this.queueUpdatedSubject.next();
  }

  async clearCompletionQueue(): Promise<void> {
    const queue = await this.getCompletionQueue();

    await Promise.all(
      queue.map(item => this.removeCompletionFromQueue(item.requestNumber)),
    );
  }

  /**
   * Повторить упавшие задачи заявки (например, после исправления прав):
   * задачи возвращаются в ожидание, процессор запускается сразу
   */
  async retryFailedForRequest(requestNumber: string): Promise<void> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    const [submissions, completions] = await Promise.all([
      this.getSubmissionQueue(owner),
      this.getCompletionQueue(owner),
    ]);

    for (const submission of submissions) {
      if (
        submission.requestNumber === requestNumber &&
        submission.status === 'failed'
      ) {
        await this.saveSubmission(
          {
            ...submission,
            status: 'pending',
            nextAttemptAt: 0,
            lastError: undefined,
          },
          owner,
        );
      }
    }

    for (const completion of completions) {
      if (
        completion.requestNumber === requestNumber &&
        completion.status === 'failed'
      ) {
        await this.saveCompletion(
          {
            ...completion,
            status: 'pending',
            nextAttemptAt: 0,
            lastError: undefined,
          },
          owner,
        );
      }
    }

    this.queueChangedSubject.next();
  }

  /**
   * Количество неотправленных задач текущего владельца.
   */
  async getQueueCount(): Promise<number> {
    const [submissions, completions] = await Promise.all([
      this.getSubmissionQueue(),
      this.getCompletionQueue(),
    ]);

    return submissions.length + completions.length;
  }
}

type StoredSubmission = Partial<QueuedWorkflowSubmission> &
  Pick<
    QueuedWorkflowSubmission,
    | 'id'
    | 'requestNumber'
    | 'workflowId'
    | 'nodeId'
    | 'formId'
    | 'body'
    | 'timestamp'
  >;

type StoredCompletion = Partial<QueuedWorkflowCompletion> &
  Pick<QueuedWorkflowCompletion, 'requestNumber' | 'timestamp'>;

/**
 * Задачи, записанные до появления полей состояния (stage/dev), дополняются значениями по умолчанию
 */
function normalizeSubmission(
  value: StoredSubmission,
): QueuedWorkflowSubmission {
  return {
    ...value,
    operationId: value.operationId ?? value.id,
    instanceKey:
      value.instanceKey ??
      workflowInstanceKeyToString(value.requestNumber, {
        requestWorkflowId: value.workflowId,
        nodeId: value.nodeId,
        formId: value.formId,
      }),
    attempts: value.attempts ?? 0,
    nextAttemptAt: value.nextAttemptAt ?? 0,
    status: value.status ?? 'pending',
  };
}

function normalizeCompletion(
  value: StoredCompletion,
): QueuedWorkflowCompletion {
  return {
    ...value,
    operationId: value.operationId ?? crypto.randomUUID(),
    attempts: value.attempts ?? 0,
    nextAttemptAt: value.nextAttemptAt ?? 0,
    status: value.status ?? 'pending',
  };
}
